import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CollectorListItem } from '../domain/collector-profile.model';
import {
  CollectorAvailabilityDay,
  CollectorAvailabilityDraftDay,
  CollectorAvailabilitySegment,
  CollectorEventBucket
} from '../my-profile/availability/availability.model';
import {
  SNAP_MINUTES,
  floorToStep
} from '../my-profile/availability/day-timeline.utils';
import { TASKS_SOURCE_TIME_ZONE } from './dc-roster.service';

const CET_DATETIME_FORMAT = `yyyy-MM-dd'T'HH:mm`;

interface CollectorEventBucketRow {
  dcDate: string;
  dcTime: string;
  dcType: string;
  count: number;
}

@Injectable({ providedIn: 'root' })
export class MyProfileService {
  private readonly collectorsBaseUrl = environment.API_HOST + '/api/collectors';
  private readonly dcRostersBaseUrl = environment.API_HOST + '/api/dc-rosters';

  constructor(private http: HttpClient, private logger: NGXLogger) {}

  getMyCollector(): Observable<CollectorListItem> {
    return this.http.get<CollectorListItem>(`${this.collectorsBaseUrl}/me`);
  }

  getEventBuckets(
    from: string,
    to: string
  ): Observable<CollectorEventBucket[]> {
    const localTimeZone = DateTime.local().zoneName;
    const cetFrom = DateTime.fromISO(from, { zone: localTimeZone })
      .startOf('day')
      .setZone(TASKS_SOURCE_TIME_ZONE)
      .toFormat('yyyy-MM-dd');
    const cetTo = DateTime.fromISO(to, { zone: localTimeZone })
      .endOf('day')
      .setZone(TASKS_SOURCE_TIME_ZONE)
      .toFormat('yyyy-MM-dd');
    const params = new HttpParams().set('cetFrom', cetFrom).set('cetTo', cetTo);

    return this.http
      .get<{ rows: CollectorEventBucketRow[] }>(
        `${this.dcRostersBaseUrl}/event-buckets`,
        { params }
      )
      .pipe(
        map((response) =>
          this.bucketRowsToLocal(response.rows ?? [], from, to, localTimeZone)
        )
      );
  }

  getAvailability(
    from: string,
    to: string
  ): Observable<CollectorAvailabilityDay[]> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http
      .get<{ days: CollectorAvailabilityDay[] }>(
        `${this.collectorsBaseUrl}/me/availability`,
        { params }
      )
      .pipe(map((response) => response.days ?? []));
  }

  saveAvailability(
    days: CollectorAvailabilityDraftDay[]
  ): Observable<CollectorAvailabilityDay[]> {
    const localTimeZone = DateTime.local().zoneName;
    const payload = {
      days: days.map((day) => ({
        localDate: day.date,
        localTimeZone,
        dayOff: day.dayOff,
        segments: day.dayOff
          ? []
          : day.segments.map((segment) =>
              this.toCetSegment(day.date, localTimeZone, segment)
            )
      }))
    };

    return this.http
      .put<{ days: CollectorAvailabilityDay[] }>(
        `${this.collectorsBaseUrl}/me/availability`,
        payload
      )
      .pipe(map((response) => response.days ?? []));
  }

  private toCetSegment(
    localDate: string,
    localTimeZone: string,
    segment: { startTime: number; endTime: number }
  ): CollectorAvailabilitySegment {
    const localStart = DateTime.fromISO(localDate, {
      zone: localTimeZone
    })
      .startOf('day')
      .plus({ minutes: segment.startTime });
    const localEnd = DateTime.fromISO(localDate, {
      zone: localTimeZone
    })
      .startOf('day')
      .plus({ minutes: segment.endTime });

    return {
      startCet: localStart
        .setZone(TASKS_SOURCE_TIME_ZONE)
        .toFormat(CET_DATETIME_FORMAT),
      endCet: localEnd
        .setZone(TASKS_SOURCE_TIME_ZONE)
        .toFormat(CET_DATETIME_FORMAT),
      startLocalMin: segment.startTime,
      endLocalMin: segment.endTime
    };
  }

  private bucketRowsToLocal(
    rows: CollectorEventBucketRow[],
    localFrom: string,
    localTo: string,
    localTimeZone: string
  ): CollectorEventBucket[] {
    const byKey = new Map<string, CollectorEventBucket>();

    for (const row of rows) {
      const dcTime =
        typeof row.dcTime === 'string' && row.dcTime.trim()
          ? row.dcTime.trim()
          : '00:00';
      const localDateTime = DateTime.fromISO(`${row.dcDate}T${dcTime}`, {
        zone: TASKS_SOURCE_TIME_ZONE
      }).setZone(localTimeZone);

      if (!localDateTime.isValid) {
        this.logger.warn(
          `Skipping invalid event bucket row: ${JSON.stringify({
            dcDate: row.dcDate,
            dcTime,
            dcType: row.dcType,
            count: row.count,
            invalidReason: localDateTime.invalidReason,
            invalidExplanation: localDateTime.invalidExplanation
          })}`
        );
        continue;
      }

      const localDate = localDateTime.toFormat('yyyy-MM-dd');
      if (localDate < localFrom || localDate > localTo) {
        continue;
      }

      const bucketMinute = floorToStep(localDateTime.minute, SNAP_MINUTES);
      const key = `${localDate}|${localDateTime.hour}|${bucketMinute}`;
      const bucket = byKey.get(key) ?? {
        date: localDate,
        hour: localDateTime.hour,
        minute: bucketMinute,
        count: 0,
        breakdown: {}
      };

      bucket.count += row.count;
      bucket.breakdown[row.dcType] =
        (bucket.breakdown[row.dcType] ?? 0) + row.count;
      byKey.set(key, bucket);
    }

    return [...byKey.values()].sort((left, right) =>
      left.date === right.date
        ? left.hour === right.hour
          ? left.minute - right.minute
          : left.hour - right.hour
        : left.date.localeCompare(right.date)
    );
  }
}
